import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Loader2, User, CreditCard, Calendar, Upload, CheckCircle, Banknote } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusBadge = (status) => {
  const map = {
    pending: { className: 'bg-yellow-100 text-yellow-800', text: 'Menunggu Pembayaran' },
    waiting_confirmation: { className: 'bg-blue-100 text-blue-800', text: 'Menunggu Konfirmasi' },
    paid: { className: 'bg-green-100 text-green-800', text: 'Dibayar' },
    dp: { className: 'bg-blue-100 text-blue-800', text: 'DP' },
    confirmed: { className: 'bg-blue-100 text-blue-800', text: 'Dikonfirmasi' },
    completed: { className: 'bg-green-100 text-green-800', text: 'Selesai' },
    cancelled: { className: 'bg-red-100 text-red-800', text: 'Dibatalkan' },
    failed: { className: 'bg-red-100 text-red-800', text: 'Gagal' },
  };
  return map[status] || { className: 'bg-gray-100 text-gray-800', text: status };
};

const OrderDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [transferForm, setTransferForm] = useState({
    transfer_date: new Date().toISOString().split('T')[0],
    bank_name: '',
    account_holder: ''
  });

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const res = await axios.get(`${API}/orders/${id}`);
        setOrder(res.data);
      } catch (err) {
        toast.error('Gagal memuat detail pesanan');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/settings/`);
        setSettings(res.data);
        // Pre-fill bank info from active bank account
        const activeBank = (res.data.bank_accounts || []).find(b => b.is_active);
        if (activeBank) {
          setTransferForm(prev => ({
            ...prev,
            bank_name: activeBank.bank_name
          }));
        }
      } catch {}
    };
    fetchOrder();
    fetchSettings();
  }, [id, navigate]);

  const handleCancel = async () => {
    if (!window.confirm('Yakin ingin membatalkan pesanan ini?')) return;
    try {
      await axios.put(`${API}/orders/${id}/cancel`);
      toast.success('Pesanan berhasil dibatalkan');
      setOrder(prev => ({ ...prev, booking_status: 'cancelled' }));
    } catch (err) {
      toast.error('Gagal membatalkan: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleMarkPaid = async () => {
    if (!window.confirm('Konfirmasi bahwa Anda sudah melakukan pembayaran?')) return;
    try {
      await axios.put(`${API}/orders/${id}/mark-paid`);
      toast.success('Status pembayaran diperbarui. Menunggu konfirmasi admin.');
      setOrder(prev => ({ ...prev, payment_status: 'waiting_confirmation' }));
    } catch (err) {
      toast.error('Gagal: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleUploadProof = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Ukuran file maksimal 5MB');
      return;
    }
    if (!transferForm.account_holder.trim()) {
      toast.error('Nama pemilik rekening wajib diisi');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('order_id', id);
    formData.append('transfer_date', transferForm.transfer_date);
    formData.append('bank_name', transferForm.bank_name);
    formData.append('account_holder', transferForm.account_holder);

    try {
      const res = await axios.post(`${API}/upload/payment-proof`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Bukti pembayaran berhasil diupload. Menunggu konfirmasi admin.');
      setOrder(prev => ({
        ...prev,
        payment_proof: res.data.url,
        payment_proof_filename: file.name,
        transfer_date: transferForm.transfer_date,
        transfer_bank_name: transferForm.bank_name,
        transfer_account_holder: transferForm.account_holder
      }));
    } catch (err) {
      toast.error('Gagal upload: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen py-12 bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!order) return null;

  const fmt = (p) => `Rp ${(p || 0).toLocaleString('id-ID')}`;
  const bStatus = statusBadge(order.booking_status);
  const pStatus = statusBadge(order.payment_status);
  const hasTax = order.tax > 0;
  const bankAccounts = settings?.bank_accounts || [];
  const activeBank = bankAccounts.find(b => b.is_active) || bankAccounts[0];

  return (
    <div className="min-h-screen py-12 bg-background">
      <div className="container mx-auto px-4 max-w-3xl">
        <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" /> Kembali
        </Button>

        <h1 className="text-3xl font-bold font-['Outfit'] mb-2">Detail Pesanan</h1>
        <p className="text-muted-foreground mb-6">{order.order_number}</p>

        {/* Status */}
        <div className="flex gap-2 mb-6">
          <Badge className={bStatus.className}>Booking: {bStatus.text}</Badge>
          <Badge className={pStatus.className}>Pembayaran: {pStatus.text}</Badge>
        </div>

        {/* Order Info */}
        <Card className="rounded-2xl mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Calendar className="h-5 w-5" /> Info Pesanan</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">No. Pesanan</span>
              <span className="font-medium">{order.order_number}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Paket</span>
              <span className="font-medium">{order.package_name || order.package_id}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Jumlah Jamaah</span>
              <span className="font-medium">{order.total_pilgrims} orang</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Tanggal Pemesanan</span>
              <span className="font-medium">{new Date(order.created_at).toLocaleDateString('id-ID')}</span>
            </div>
          </CardContent>
        </Card>

        {/* Payment Breakdown */}
        <Card className="rounded-2xl mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><CreditCard className="h-5 w-5" /> Rincian Pembayaran</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span>{fmt(order.subtotal)}</span>
            </div>
            {hasTax && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">PPN</span>
                <span>{fmt(order.tax)}</span>
              </div>
            )}
            {order.discount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Diskon Voucher{order.voucher_code ? ` (${order.voucher_code})` : ''}</span>
                <span className="text-green-600">-{fmt(order.discount)}</span>
              </div>
            )}
            <div className="border-t pt-3 flex justify-between font-bold text-lg">
              <span>Total</span>
              <span>{fmt(order.total_amount)}</span>
            </div>
            {order.payment_status === 'dp' && (
              <>
                <div className="flex justify-between text-sm text-blue-600">
                  <span>DP Dibayar</span>
                  <span>-{fmt(order.dp_amount)}</span>
                </div>
                <div className="flex justify-between text-sm font-semibold">
                  <span>Sisa Pelunasan</span>
                  <span>{fmt(order.remaining_amount)}</span>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Transfer Instructions (show when pending) */}
        {order.payment_status === 'pending' && (
          <Card className="rounded-2xl mb-6 border-2 border-emerald-200 dark:border-emerald-800">
            <CardHeader className="bg-emerald-50 dark:bg-emerald-950 rounded-t-xl">
              <CardTitle className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300">
                <CheckCircle className="h-5 w-5" /> Instruksi Pembayaran
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className="text-sm space-y-1">
                <p className="font-semibold mb-2">Silakan transfer ke rekening berikut:</p>
                {activeBank ? (
                  <div className="bg-muted p-4 rounded-lg space-y-2">
                    <p className="text-xs text-muted-foreground">{activeBank.bank_name}</p>
                    <p className="text-xl font-bold font-mono">{activeBank.account_number}</p>
                    <p className="text-sm">a.n. {activeBank.account_name}</p>
                  </div>
                ) : (
                  <div className="bg-muted p-4 rounded-lg">
                    <p className="text-muted-foreground">Informasi rekening belum tersedia</p>
                  </div>
                )}
                <p className="font-semibold mt-4 mb-1">Total yang harus dibayar:</p>
                <p className="text-2xl font-bold text-primary font-mono">{fmt(order.payment_type === 'dp' ? order.dp_amount : order.total_amount)}</p>
                {order.payment_type === 'dp' && (
                  <p className="text-sm text-blue-600 mt-1">Ini adalah DP sebesar {fmt(order.dp_amount)}. Sisa pelunasan {fmt(order.remaining_amount)} akan dibayar setelah admin mengkonfirmasi DP Anda.</p>
                )}
                <p className="text-xs text-muted-foreground mt-2">
                  Setelah transfer, klik tombol "Saya Sudah Bayar" di bawah atau konfirmasi ke admin via WhatsApp:{' '}
                  {settings?.contact_phone_1 && (
                    <a href={`https://wa.me/${settings.contact_phone_1.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">
                      {settings.contact_phone_1}
                    </a>
                  )}
                  {settings?.contact_phone_2 && (
                    <>
                      {' '} / {' '}
                      <a href={`https://wa.me/${settings.contact_phone_2.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">
                        {settings.contact_phone_2}
                      </a>
                    </>
                  )}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Waiting Confirmation notice */}
        {order.payment_status === 'waiting_confirmation' && (
          <Card className="rounded-2xl mb-6 border-2 border-blue-200">
            <CardContent className="py-4 text-center">
              <CheckCircle className="h-8 w-8 mx-auto mb-2 text-blue-500" />
              <p className="font-semibold text-blue-700"> Pembayaran</p>
              <p className="text-sm text-muted-foreground mt-1">Admin akan segera memverifikasi pembayaran Anda</p>
            </CardContent>
          </Card>
        )}

        {/* Upload Payment Proof */}
        {(order.payment_status === 'pending' || order.payment_status === 'waiting_confirmation' || order.payment_status === 'dp') && (
          <Card className="rounded-2xl mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Upload className="h-5 w-5" /> Upload Bukti Transfer</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Tanggal Transfer</Label>
                  <Input
                    type="date"
                    value={transferForm.transfer_date}
                    onChange={(e) => setTransferForm(prev => ({ ...prev, transfer_date: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nama Bank</Label>
                  <Input
                    value={transferForm.bank_name}
                    onChange={(e) => setTransferForm(prev => ({ ...prev, bank_name: e.target.value }))}
                    placeholder="Contoh: BSI, BCA, Mandiri"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nama Pemilik Rekening *</Label>
                  <Input
                    value={transferForm.account_holder}
                    onChange={(e) => setTransferForm(prev => ({ ...prev, account_holder: e.target.value }))}
                    placeholder="Nama sesuai rekening"
                    required
                  />
                </div>
              </div>

              <Label htmlFor="proof" className="cursor-pointer block">
                <div className="border-2 border-dashed border-muted-foreground/30 rounded-xl p-8 text-center hover:border-primary/50 transition-colors">
                  <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">Klik untuk upload bukti transfer (max 5MB, JPG/PNG/PDF)</p>
                </div>
                <Input id="proof" type="file" accept="image/*,.pdf" onChange={handleUploadProof} disabled={uploading} className="hidden" />
              </Label>
              {uploading && <div className="flex items-center justify-center mt-4"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Uploading...</div>}
            </CardContent>
          </Card>
        )}

        {/* Show uploaded proof */}
        {order.payment_proof && (
          <Card className="rounded-2xl mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><CheckCircle className="h-5 w-5 text-green-600" /> Bukti Pembayaran</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {order.payment_proof.startsWith('data:') ? (
                <div className="rounded-lg overflow-hidden border">
                  {order.payment_proof.includes('image') ? (
                    <img src={order.payment_proof} alt="Bukti transfer" className="max-w-full h-auto max-h-64 object-contain" />
                  ) : (
                    <a href={order.payment_proof} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline text-sm p-4 block">
                      📄 Lihat PDF bukti pembayaran
                    </a>
                  )}
                </div>
              ) : (
                <a href={order.payment_proof} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline text-sm">
                  Lihat bukti pembayaran
                </a>
              )}
              {order.transfer_date && (
                <div className="text-sm text-muted-foreground">
                  Tanggal transfer: <span className="font-medium">{order.transfer_date}</span>
                </div>
              )}
              {order.transfer_bank_name && (
                <div className="text-sm text-muted-foreground">
                  Bank: <span className="font-medium">{order.transfer_bank_name}</span>
                </div>
              )}
              {order.transfer_account_holder && (
                <div className="text-sm text-muted-foreground">
                  Rekening atas nama: <span className="font-medium">{order.transfer_account_holder}</span>
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-1">Menunggu konfirmasi admin</p>
            </CardContent>
          </Card>
        )}

        {/* Jamaah Data */}
        {order.jamaahs && order.jamaahs.length > 0 && (
          <Card className="rounded-2xl mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><User className="h-5 w-5" /> Data Jamaah</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {order.jamaahs.map((j, i) => (
                <div key={i} className="p-3 border rounded-lg space-y-1 text-sm">
                  <p className="font-medium">{j.full_name}</p>
                  <p className="text-muted-foreground">NIK: {j.nik} • {j.gender === 'male' ? 'Laki-laki' : 'Perempuan'}</p>
                  <p className="text-muted-foreground">📞 {j.phone} • ✉️ {j.email}</p>
                  {j.passport_number && <p className="text-muted-foreground">Paspor: {j.passport_number}</p>}
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        {order.booking_status === 'pending' && order.payment_status === 'pending' && (
          <div className="flex gap-3">
            <Button variant="destructive" onClick={handleCancel} className="rounded-full">
              Batalkan Pesanan
            </Button>
            <Button
              className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white flex-1"
              onClick={handleMarkPaid}
            >
              <Banknote className="h-5 w-5 mr-2" /> Saya Sudah Bayar
            </Button>
          </div>
        )}

        {/* Pelunasan action */}
        {order.payment_status === 'dp' && (
          <div className="flex flex-col gap-3">
            <div className="bg-blue-50 dark:bg-blue-950 border-2 border-blue-200 dark:border-blue-800 rounded-2xl p-4">
              <p className="font-semibold text-blue-700 dark:text-blue-300">Sisa Pelunasan: {fmt(order.remaining_amount)}</p>
              <p className="text-sm text-muted-foreground mt-1">Transfer sisa pelunasan ke rekening di atas, lalu upload bukti & klik "Saya Sudah Bayar".</p>
            </div>
            <Button
              className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white flex-1"
              onClick={handleMarkPaid}
            >
              <Banknote className="h-5 w-5 mr-2" /> Lunasi Sekarang
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderDetailPage;
